import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';

import { HttpClient, useHttpClient } from '@bonusly/http-client';

import {
	CompanyNavbarSettingsResponse,
	NavbarConfiguration,
	NavbarItemId,
	NavbarPreferences,
	NavbarSettings,
	ResetCompanySettingsResponse,
	ResetUserPreferencesResponse,
} from '../schemas/navigation';
import { headers } from './meta';

enum QueryKey {
	NavbarConfiguration = 'navigation:configuration',
	UserNavbarPreferences = 'navigation:user-preferences',
	CompanyNavbarSettings = 'navigation:company-settings',
}

export const navbarConfigurationQueryKey = () => [QueryKey.NavbarConfiguration];
export const userNavbarPreferencesQueryKey = () => [QueryKey.UserNavbarPreferences];
export const companyNavbarSettingsQueryKey = () => [QueryKey.CompanyNavbarSettings];

export const navigationConfigurationPath = (prefix = '') => `${prefix}/navigation/configuration`;
export const userNavbarPreferencesPath = (prefix = '') => `${prefix}/navigation/user_preferences`;
export const companyNavbarSettingsPath = (prefix = '') => `${prefix}/navigation/company_settings`;

type UpdateNavbarItemsPayload = {
	mainNavItems: NavbarItemId[];
	moreMenuItems: NavbarItemId[];
};

const toUpdatePayload = ({ mainNavItems, moreMenuItems }: UpdateNavbarItemsPayload) => ({
	main_nav_items: mainNavItems,
	more_menu_items: moreMenuItems,
});

const createNavbarConfigurationFetcher = (http: HttpClient) => async () => {
	const response = await http.get<unknown>(navigationConfigurationPath(), { headers });
	return NavbarConfiguration.parse(response.data);
};

export const useNavbarConfiguration = () => {
	return useQuery({
		queryKey: navbarConfigurationQueryKey(),
		queryFn: createNavbarConfigurationFetcher(useHttpClient()),
	});
};

export const useUpdateUserNavbarPreferences = () => {
	const http = useHttpClient();
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: async (payload: UpdateNavbarItemsPayload) => {
			const response = await http.put<unknown>(userNavbarPreferencesPath(), toUpdatePayload(payload), {
				headers,
			});
			return NavbarPreferences.parse(response.data);
		},
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: userNavbarPreferencesQueryKey() });
			queryClient.invalidateQueries({ queryKey: navbarConfigurationQueryKey() });
		},
	});
};

export const useResetUserNavbarPreferences = () => {
	const http = useHttpClient();
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: async () => {
			const response = await http.delete<unknown>(userNavbarPreferencesPath(), { headers });
			return ResetUserPreferencesResponse.parse(response.data);
		},
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: userNavbarPreferencesQueryKey() });
			queryClient.invalidateQueries({ queryKey: navbarConfigurationQueryKey() });
		},
	});
};

export const useCompanyNavbarSettings = (enabled = true) => {
	const http = useHttpClient();

	return useQuery({
		queryKey: companyNavbarSettingsQueryKey(),
		queryFn: async () => {
			const response = await http.get<unknown>(companyNavbarSettingsPath(), { headers });
			return CompanyNavbarSettingsResponse.parse(response.data);
		},
		enabled,
	});
};

export const useUpdateCompanyNavbarSettings = () => {
	const http = useHttpClient();
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: async (payload: UpdateNavbarItemsPayload) => {
			const response = await http.put<unknown>(companyNavbarSettingsPath(), toUpdatePayload(payload), {
				headers,
			});
			return NavbarSettings.parse(response.data);
		},
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: companyNavbarSettingsQueryKey() });
			queryClient.invalidateQueries({ queryKey: navbarConfigurationQueryKey() });
		},
	});
};

export const useResetCompanyNavbarSettings = () => {
	const http = useHttpClient();
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: async () => {
			const response = await http.delete<unknown>(companyNavbarSettingsPath(), { headers });
			return ResetCompanySettingsResponse.parse(response.data);
		},
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: companyNavbarSettingsQueryKey() });
			queryClient.invalidateQueries({ queryKey: navbarConfigurationQueryKey() });
		},
	});
};

export const useInvalidateNavbarConfiguration = () => {
	const queryClient = useQueryClient();
	return useCallback(() => queryClient.invalidateQueries({ queryKey: navbarConfigurationQueryKey() }), [queryClient]);
};
